import * as React from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import { Typography, ThemeProvider } from '@material-ui/core';
import classNames from 'classnames';
import { theme } from '../../../theme/theme';
import { useItemGridStyles } from './styles';
import { useSelector, useDispatch } from 'react-redux';
import GroupedHeader from './GroupedHeader';
import { getColumns } from './utils';
import { getTopBottomSalesGrid } from '../../../redux/actions/itemDrillDownActions';

export function standardizeComparison(rowValue, orderBy) {
	if (rowValue[orderBy] === 'NA') {
		return Number.NEGATIVE_INFINITY;
	} else if (isNaN(Number(rowValue[orderBy]))) {
		return rowValue[orderBy];
	} else {
		return Number(rowValue[orderBy]);
	}
}

export function descendingComparator(a, b, orderBy) {
	const aVal = standardizeComparison(a, orderBy);
	const bVal = standardizeComparison(b, orderBy);

	if (bVal < aVal) {
		return -1;
	}
	if (bVal > aVal) {
		return 1;
	}
	return 0;
}

export function getComparator(order, orderBy) {
	return order === 'desc'
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
export function stableSort(array, comparator) {
	const stabilizedThis = array?.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		return a[1] - b[1];
	});
	return stabilizedThis?.map((el) => el[0]);
}

export default function ColumnGroupingTable(props) {
	const {
		columns,
		data,
		valueUnit,
		handleRowClick,
		height,
		selectedTab,
		fontSize,
		paddingRight,
		paddingLeft,
		subHeaders,
		marginLeft,
		numberOfRecords,
		isStore,
	} = props;
	const classes = useItemGridStyles({
		fontSize: fontSize,
		paddingLeft: paddingLeft,
		paddingRight: paddingRight,
	});
	const cx = classNames;

	const [order, setOrder] = React.useState('asc');
	const [orderBy, setOrderBy] = React.useState('netSales');
	const dispatch = useDispatch();
	const selectedFilters = useSelector(
		(state) => state.salesReducer.selectedFilters
	);
	const { selectedFilter } = selectedFilters;

	const handleRequestSort = (event, property) => {
		if (property === 'netSales') {
			dispatch(getTopBottomSalesGrid(order === 'asc' ? 'desc' : 'asc'));
		}
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const omniFilter = selectedFilter;
	const dataMap =
		orderBy === 'netSales'
			? data
			: stableSort(data, getComparator(order, orderBy));

	const tableBodyData = getColumns(columns, omniFilter);

	return (
		<ThemeProvider theme={theme}>
			<Box sx={{ width: '100%' }}>
				<Paper sx={{ width: '100%', mb: 2, boxShadow: 'none', elevation: '0' }}>
					<TableContainer sx={{ maxHeight: height, width: '100%' }}>
						<Table
							role="grid"
							stickyHeader
							sx={{ width: '100%' }}
							aria-labelledby="tableTitle"
							aria-label="Column Grouped Table"
							tabIndex={0}>
							<GroupedHeader
								columns={columns}
								order={order}
								orderBy={orderBy}
								onRequestSort={handleRequestSort}
								valueUnit={valueUnit}
								omniFilter={omniFilter}
								customized={true}
								fontSize={fontSize}
								paddingLeft={paddingLeft}
								paddingRight={paddingRight}
								subHeaders={subHeaders}
								dataLength={data?.length}
								totalColumns={tableBodyData?.length}
								numberOfRecords={numberOfRecords}
								isStore={isStore}
							/>

							<TableBody>
								{dataMap?.map((row, idx) => {
									return (
										<TableRow
											hover
											key={idx}
											onClick={() => handleRowClick(row)}
											sx={{ verticalAlign: 'baseline' }}>
											{tableBodyData?.map((column, index) => {
												const cellData = row[column.id];
												return (
													<TableCell
														key={column.id}
														align={column.align}
														sx={{
															padding: '8.75px',
															letterSpacing: '0.01071em',
															lineHeight: 'unset',
															maxWidth: index === 0 ? '300px' : null,
															cursor:
																selectedTab === 'Item' ? 'pointer' : 'default',
														}}>
														<Typography
															style={
																index === 0
																	? {
																			display: 'inline-block',
																			marginLeft: marginLeft,
																			maxWidth: '300px',
																			verticalAlign: 'top',
																			overflow: 'hidden',
																			whiteSpace: 'nowrap',
																			textOverflow: 'ellipsis',
																	  }
																	: {}
															}
															className={cx(
																classes.tableCell,
																classes.tableCellFonts
															)}>
															{typeof cellData === 'number'
																? cellData.toLocaleString()
																: cellData}
														</Typography>
													</TableCell>
												);
											})}
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</Box>
		</ThemeProvider>
	);
}

ColumnGroupingTable.defaultProps = {
	fontSize: '16px',
	paddingLeft: '16px',
	paddingRight: '16px',
	subHeaders: false,
	marginLeft: '6px',
};

ColumnGroupingTable.propTypes = {
	height: PropTypes.number,
	width: PropTypes.number,
	columns: PropTypes.arrayOf(PropTypes.shape({})),
	data: PropTypes.arrayOf(PropTypes.shape({})),
	valueUnit: PropTypes.string,
	handleRowClick: PropTypes.func.isRequired,
	selectedTab: PropTypes.string,
	fontSize: PropTypes.string,
	paddingLeft: PropTypes.string,
	paddingRight: PropTypes.string,
	subHeaders: PropTypes.bool,
	marginLeft: PropTypes.string,
	numberOfRecords: PropTypes.number,
	isStore: PropTypes.bool,
};